import {
  registerAuthConfigWithOptions,
  TokenAdditionalInfo,
  UserIdentityEvent as originIdentityEvent,
  getIdentity
} from '@cimpress-technology/authfront-sdk-core'

import { CookieTokenStorage } from './utils/storage'
import { OCI_BASE_URL } from '../common/constants'
import type { UserIdentityEvent, ErrorAndResultCallbackFn } from '../common/types'
import { getWindow, transformAuthIdentityEventToUserIdentityEvent, isEmployeeConnection, getCookie, deleteCookie } from './utils/helper'
import { exchangeRefreshTokenForAccessToken } from './utils/tokenRefresh'
import { AuthService } from './auth'
import { AuthenticationError } from './errors'

export type Config = {
  clientId: string
  oauthBaseURL?: string
  tokenAdditionalInfo?: TokenAdditionalInfo
  culture: string
  tenant: string
  connection: string
  options?: any
}

export const cookieTokenStorage = new CookieTokenStorage()

function saveTokensFromResponse (response: NonNullable<Awaited<ReturnType<typeof exchangeRefreshTokenForAccessToken>>>): void {
  const { accessToken, expiresAt, tokenType, additionalInfo, refreshToken } = response

  const expiresAtDate = new Date()
  expiresAtDate.setTime(Date.now() + expiresAt * 1000)

  const refreshTokenExpiresIn = new Date((additionalInfo?.refreshTokenExpiresIn ?? 0) * 1000)

  cookieTokenStorage.saveAccessToken(accessToken, expiresAtDate, tokenType)
  cookieTokenStorage.saveAdditionalInfo(additionalInfo)
  cookieTokenStorage.saveRefreshToken(refreshToken, refreshTokenExpiresIn, true)
}

export function init (
  options: any,
  callback: (userIdentity: UserIdentityEvent) => void,
  errorCallback: (error: Error) => void
): void {
  const authfrontContainer = getWindow().authfrontContainer

  if (authfrontContainer) {
    if (authfrontContainer.configRegistrationStatus === 'complete') {
      const identity = getIdentity()
      callback(transformAuthIdentityEventToUserIdentityEvent(identity))
    } else if (authfrontContainer.configRegistrationStatus === 'failure') {
      errorCallback(new Error('Failed to initialize vp/auth.'))
    }
  }

  AuthService.eventTargetInstance.addEventListener('userIdentity', (e: any) => {
    const userDetails = e.detail
    callback(transformAuthIdentityEventToUserIdentityEvent(userDetails))
  })

  AuthService.eventTargetInstance.addEventListener('userIdentityError', (e: any) => {
    const { message, statusCode } = e.detail
    errorCallback(new AuthenticationError(
      `Error during authentication: ${message ?? 'Unknown error'} (Status code: ${statusCode ?? 'N/A'})`,
      statusCode
    ))
  })
}

export async function initWithConfig (
  config: Config,
  callback: ErrorAndResultCallbackFn<originIdentityEvent>
): Promise<void> {
  const window = getWindow()
  window.ubikAuthObject = config

  const { clientId, oauthBaseURL, tokenAdditionalInfo, connection, tenant } = config

  cookieTokenStorage.setTenant(tenant)

  const configuredClientId = cookieTokenStorage.getClientId()

  if (tokenAdditionalInfo) {
    cookieTokenStorage.saveAdditionalInfo(tokenAdditionalInfo)
  } else {
    const refreshToken = cookieTokenStorage.retrieveRefreshToken()
    const additionalInfo = cookieTokenStorage.retrieveAdditionalInfo()
    const anonReferenceToken = getCookie('anon')

    if (!additionalInfo) {
      if (refreshToken) {
        const response = await exchangeRefreshTokenForAccessToken(
          configuredClientId || clientId,
          oauthBaseURL ?? OCI_BASE_URL,
          refreshToken
        )

        if (response) {
          saveTokensFromResponse(response)
        }
      } else if (anonReferenceToken) {
        try {
          const parsedAnonRefreshTokenInfo = JSON.parse(anonReferenceToken)

          const response = await exchangeRefreshTokenForAccessToken(
            configuredClientId || clientId,
            oauthBaseURL ?? OCI_BASE_URL,
            parsedAnonRefreshTokenInfo.nonce
          )

          if (response) {
            saveTokensFromResponse(response)
          }
        } catch (error) {
          console.error('Error parsing anon reference token or exchanging it for access token:', error)
        } finally {
          deleteCookie('anon')
        }
      }
    }
  }

  registerAuthConfigWithOptions(
    {
      clientId: configuredClientId || clientId,
      oauthBaseURL: oauthBaseURL || OCI_BASE_URL,
      useBuiltInCallback: true,
      generateAnonIdentity: !connection || !isEmployeeConnection(connection),
      storeRefreshTokenAsCookie: true,
      extendBrowserSession: true
    }, callback)

  if (new URLSearchParams(window.location.search).get('askUserToLogin')) {
    const auth = new AuthService()
    auth.signIn([], [], {})
  }
}
